import React, { useState, useEffect } from 'react';
import { Users, Shield, Trash2, Edit3, Search, DollarSign, Package, Target, TrendingUp, Calendar, Megaphone, ChevronLeft, ChevronRight, X, Save, Eye, EyeOff, Lock, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const superadminAPI = {
  getUsers: (page, limit, search) => api.get('/superadmin/users', { params: { page, limit, search } }),
  getUser: (id) => api.get(`/superadmin/users/${id}`),
  updateUser: (id, data) => api.put(`/superadmin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/superadmin/users/${id}`),
  changePassword: (data) => api.put('/superadmin/change-password', data),
  getStats: () => api.get('/superadmin/stats'),
};

function StatCard({ icon: Icon, label, value, color = 'text-zinc-400', bg = 'bg-zinc-800' }) {
  return (
    <div className="p-4 bg-zinc-900 rounded-xl border border-zinc-800">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center`}>
          <Icon size={13} className={color} />
        </div>
        <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">{label}</span>
      </div>
      <p className="text-2xl font-display font-black text-white">{value}</p>
    </div>
  );
}

export default function SuperAdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPwChange, setShowPwChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [showPw, setShowPw] = useState(false);

  const loadUsers = async (p = 1, s = '') => {
    try {
      const res = await superadminAPI.getUsers(p, 30, s);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Superadmin access required');
      else toast.error('Failed to load users');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes] = await Promise.all([
          superadminAPI.getStats().catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
      } catch {}
      await loadUsers();
      setLoading(false);
    };
    load();
  }, []);

  const handleSearch = () => loadUsers(1, search);

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, email: u.email, role: u.role || 'member', wallet_balance: u.wallet_balance || 0, vip_tier: u.vip_tier || 'bronze', points: u.points || 0, is_superadmin: u.is_superadmin || false, password: '' });
  };

  const handleSave = async () => {
    if (!editUser) return;
    const payload = { ...editForm };
    if (!payload.password) delete payload.password;
    payload.wallet_balance = parseFloat(payload.wallet_balance) || 0;
    payload.points = parseInt(payload.points) || 0;
    try {
      await superadminAPI.updateUser(editUser.id, payload);
      toast.success('User updated');
      setEditUser(null);
      loadUsers(page, search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const handleDelete = async (userId) => {
    try {
      await superadminAPI.deleteUser(userId);
      toast.success('User deleted');
      setDeleteConfirm(null);
      loadUsers(page, search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handlePwChange = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('Both fields required'); return; }
    try {
      await superadminAPI.changePassword(pwForm);
      toast.success('Password changed');
      setShowPwChange(false);
      setPwForm({ current_password: '', new_password: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>;

  const s = stats || {};

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="superadmin-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-red-950 via-zinc-950 to-zinc-900 border-b border-red-900/30">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                <Shield size={20} className="text-white" />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl text-white">Super Admin</h1>
                <p className="text-xs text-red-400/60">{user?.email} — Full platform control</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowPwChange(!showPwChange)} variant="outline" size="sm" className="border-red-800 text-red-400 hover:bg-red-950 text-xs">
                <Lock size={12} className="mr-1" /> Change Password
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-4">
        {/* Password change panel */}
        <AnimatePresence>
          {showPwChange && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-4">
              <div className="p-4 bg-zinc-900 rounded-2xl border border-red-900/30 flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-zinc-400 text-xs">Current Password</Label>
                  <Input type={showPw ? 'text' : 'password'} value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="current-pw" />
                </div>
                <div className="flex-1">
                  <Label className="text-zinc-400 text-xs">New Password</Label>
                  <Input type={showPw ? 'text' : 'password'} value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="new-pw" />
                </div>
                <Button onClick={() => setShowPw(!showPw)} variant="ghost" size="sm" className="text-zinc-500">{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</Button>
                <Button onClick={handlePwChange} size="sm" className="bg-red-600 text-white font-bold text-xs" data-testid="save-pw-btn">Save</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[{ key: 'stats', label: 'Platform Stats', icon: BarChart3 }, { key: 'users', label: 'User Management', icon: Users }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all ${tab === t.key ? 'bg-red-600 text-white' : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'}`} data-testid={`tab-${t.key}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {tab === 'stats' && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard icon={Users} label="Users" value={s.total_users || 0} color="text-blue-400" bg="bg-blue-500/10" />
            <StatCard icon={DollarSign} label="Revenue" value={formatCurrency(s.total_revenue || 0)} color="text-emerald-400" bg="bg-emerald-500/10" />
            <StatCard icon={Target} label="Funded" value={formatCurrency(s.total_funded || 0)} color="text-[#b8ff00]" bg="bg-[#b8ff00]/10" />
            <StatCard icon={Package} label="Products" value={s.total_products || 0} color="text-amber-400" bg="bg-amber-500/10" />
            <StatCard icon={TrendingUp} label="Projects" value={s.total_projects || 0} color="text-violet-400" bg="bg-violet-500/10" />
            <StatCard icon={Calendar} label="Events" value={s.total_events || 0} color="text-rose-400" bg="bg-rose-500/10" />
            <StatCard icon={Megaphone} label="Campaigns" value={s.total_campaigns || 0} color="text-cyan-400" bg="bg-cyan-500/10" />
            <StatCard icon={DollarSign} label="Investments" value={s.total_investments || 0} color="text-amber-400" bg="bg-amber-500/10" />
            <StatCard icon={Package} label="Orders" value={s.total_orders || 0} color="text-emerald-400" bg="bg-emerald-500/10" />
            <StatCard icon={Users} label="New (7d)" value={s.recent_signups || 0} color="text-[#b8ff00]" bg="bg-[#b8ff00]/10" />
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div>
            {/* Search */}
            <div className="flex gap-2 mb-4">
              <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search by name or email..." className="bg-zinc-900 border-zinc-800 text-white flex-1" data-testid="user-search" />
              <Button onClick={handleSearch} className="bg-zinc-800 text-white"><Search size={14} /></Button>
            </div>

            <div className="text-xs text-zinc-500 mb-2">{total} users · Page {page} of {pages}</div>

            {/* User table */}
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Email</div>
                <div className="col-span-1">Role</div>
                <div className="col-span-1">Wallet</div>
                <div className="col-span-1">Orders</div>
                <div className="col-span-1">Projects</div>
                <div className="col-span-1">Followers</div>
                <div className="col-span-2">Actions</div>
              </div>
              {users.map(u => (
                <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800/50 hover:bg-zinc-800/30 items-center" data-testid={`user-row-${u.id}`}>
                  <div className="col-span-3 flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-zinc-400">{u.name?.[0] || '?'}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">{u.name}</p>
                      {u.is_superadmin && <span className="text-[8px] bg-red-500/20 text-red-400 px-1 rounded font-bold">ADMIN</span>}
                    </div>
                  </div>
                  <div className="col-span-2 text-xs text-zinc-400 truncate">{u.email}</div>
                  <div className="col-span-1"><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400 font-bold">{u.role || 'member'}</span></div>
                  <div className="col-span-1 text-xs text-emerald-400 font-bold">{formatCurrency(u.wallet_balance || 0)}</div>
                  <div className="col-span-1 text-xs text-zinc-400">{u.order_count || 0}</div>
                  <div className="col-span-1 text-xs text-zinc-400">{u.project_count || 0}</div>
                  <div className="col-span-1 text-xs text-zinc-400">{u.follower_count || 0}</div>
                  <div className="col-span-2 flex gap-1">
                    <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors" data-testid={`edit-${u.id}`}>
                      <Edit3 size={12} />
                    </button>
                    <button onClick={() => setDeleteConfirm(u)} className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400 hover:bg-red-950 transition-colors" data-testid={`delete-${u.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button onClick={() => loadUsers(page - 1, search)} disabled={page <= 1} variant="outline" size="sm" className="border-zinc-700 text-zinc-400"><ChevronLeft size={14} /></Button>
                <span className="text-sm text-zinc-500 px-3 py-1">Page {page}</span>
                <Button onClick={() => loadUsers(page + 1, search)} disabled={page >= pages} variant="outline" size="sm" className="border-zinc-700 text-zinc-400"><ChevronRight size={14} /></Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditUser(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="edit-modal">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-lg text-white">Edit User</h2>
                <button onClick={() => setEditUser(null)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-zinc-400 text-xs">Name</Label><Input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-name" /></div>
                  <div><Label className="text-zinc-400 text-xs">Email</Label><Input value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-email" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-zinc-400 text-xs">Role</Label>
                    <select value={editForm.role || 'member'} onChange={e => setEditForm({ ...editForm, role: e.target.value })} className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm px-3 py-2">
                      <option value="member">Member</option><option value="creator">Creator</option><option value="vendor">Vendor</option><option value="investor">Investor</option><option value="buyer">Buyer</option>
                    </select>
                  </div>
                  <div><Label className="text-zinc-400 text-xs">Wallet</Label><Input type="number" value={editForm.wallet_balance} onChange={e => setEditForm({ ...editForm, wallet_balance: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-wallet" /></div>
                  <div><Label className="text-zinc-400 text-xs">Points</Label><Input type="number" value={editForm.points} onChange={e => setEditForm({ ...editForm, points: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-zinc-400 text-xs">VIP Tier</Label>
                    <select value={editForm.vip_tier || 'bronze'} onChange={e => setEditForm({ ...editForm, vip_tier: e.target.value })} className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm px-3 py-2">
                      <option value="bronze">Bronze</option><option value="silver">Silver</option><option value="gold">Gold</option><option value="platinum">Platinum</option>
                    </select>
                  </div>
                  <div><Label className="text-zinc-400 text-xs">New Password (leave blank to keep)</Label><Input type="text" value={editForm.password || ''} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="New password..." className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-password" /></div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={editForm.is_superadmin || false} onChange={e => setEditForm({ ...editForm, is_superadmin: e.target.checked })} className="accent-red-500" id="superadmin-check" />
                  <Label htmlFor="superadmin-check" className="text-red-400 text-xs font-bold">Superadmin Access</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <Button onClick={() => setEditUser(null)} variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                <Button onClick={handleSave} className="bg-[#b8ff00] text-black font-bold" data-testid="save-user-btn"><Save size={14} className="mr-1" /> Save Changes</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-zinc-900 border border-red-900/30 rounded-2xl p-6 w-full max-w-sm text-center" onClick={e => e.stopPropagation()} data-testid="delete-modal">
              <div className="w-14 h-14 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Trash2 size={24} className="text-red-400" />
              </div>
              <h3 className="font-bold text-lg text-white mb-1">Delete User</h3>
              <p className="text-sm text-zinc-400 mb-1">Permanently delete <strong className="text-white">{deleteConfirm.name}</strong>?</p>
              <p className="text-xs text-zinc-500 mb-5">{deleteConfirm.email} — This removes all their data.</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => setDeleteConfirm(null)} variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                <Button onClick={() => handleDelete(deleteConfirm.id)} className="bg-red-600 text-white font-bold" data-testid="confirm-delete-btn">Delete Forever</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
